import { useGraphStore } from '../../stores/useGraphStore'

/**
 * Extract analytics context from React stores (useGraphStore + useZustand).
 * Reads graph attributes (top nodes, clusters, gaps, bigrams)
 * and formats them as a structured plain-text string for the notes editor.
 */
export function extractGraphContextFromStore() {
    const graphData = useGraphStore.getState().graphData
    if (!graphData?.entriesAndGraphOfContext?.graph?.graphologyGraph) {
        return ''
    }

    const attrs =
        graphData.entriesAndGraphOfContext.graph.graphologyGraph.attributes ||
        {}

    let mainConceptsList = ''
    try {
        let names = []
        if (attrs.top_influential_nodes?.length) {
            names = attrs.top_influential_nodes.map(n =>
                typeof n === 'string' ? n : n.node || n.label || n
            )
        } else if (attrs.top_nodes?.length) {
            names = attrs.top_nodes.slice(0, 16)
        }
        if (names.length) {
            mainConceptsList = `<MainConcepts>: \n${names.join(
                ', '
            )}\n</MainConcepts>\n`
        }
    } catch (e) {
        console.warn('[ProjectNotes] Analytics extraction error:', e)
    }

    let mainTopicsList = ''
    try {
        const clusters = attrs.top_clusters
        if (clusters?.length) {
            const lines = clusters.slice(0, 12).map((cluster, ind) => {
                const label = cluster.aiName
                    ? cluster.aiName + ': '
                    : `${ind + 1}) `
                const nodeNames = (cluster.nodes || [])
                    .map(n => n.nodeName)
                    .join(' ')
                return label + nodeNames
            })
            mainTopicsList = `<MainTopics>: \n${lines.join(
                '\n'
            )}\n</MainTopics>\n`
        }
    } catch (e) {
        console.warn('[ProjectNotes] Analytics extraction error:', e)
    }

    let insightClustersList = ''
    try {
        const gaps = attrs.gaps
        if (gaps?.length) {
            const sortedGaps = [...gaps].sort(
                (a, b) =>
                    (b.distanceWeighedBySize || 0) -
                    (a.distanceWeighedBySize || 0)
            )
            const topGap = sortedGaps[0]
            const gapClusters = [topGap.from, topGap.to].filter(Boolean)
            const lines = gapClusters.map((cluster, ind) => {
                const label = cluster.aiName
                    ? cluster.aiName + ': '
                    : `${ind + 1}) `
                const nodeNames = (cluster.nodes || [])
                    .map(n => n.nodeName)
                    .join(' ')
                return label + nodeNames
            })
            insightClustersList = `<TopicalGap>: \n${lines.join(
                '\n'
            )}\n</TopicalGap>\n`
        }
    } catch (e) {
        console.warn('[ProjectNotes] Analytics extraction error:', e)
    }

    let topLatentNodes = ''
    try {
        if (attrs.top_nodes?.length) {
            topLatentNodes = `<ConceptualGateways> \n${attrs.top_nodes.join(
                ' '
            )}\n</ConceptualGateways>\n`
        }
    } catch (e) {
        console.warn('[ProjectNotes] Analytics extraction error:', e)
    }

    let topNgrams = ''
    try {
        const bigrams = attrs.bigrams
        if (bigrams?.length) {
            const lines = bigrams.slice(0, 12).map((bigram, i) => {
                const clean = bigram
                    .replace(/\s*\[.*?\]\s*$/, '')
                    .replace(/\s*<->\s*/, ' ')
                return `${i + 1}) ${clean}`
            })
            topNgrams = `<Relations>: \n${lines.join('\n')}\n</Relations>\n`
        }
    } catch (e) {
        console.warn('[ProjectNotes] Analytics extraction error:', e)
    }

    return (
        mainConceptsList +
        '\n\n' +
        mainTopicsList +
        '\n' +
        insightClustersList +
        '\n' +
        topLatentNodes +
        '\n\n' +
        topNgrams +
        '\n\n'
    )
}
